import {
    findSiteNavigationBlock,
    resolveSiteNavigationItems,
    siteNavigationAuthoredItems,
} from "../siteNavigation";
import { localizedStorefrontHref } from "../storefrontRouting";
import type {
    PageLink,
    PublicShopBasicData,
    PublicShopMenu,
    PublicShopMenuItem,
    ShopTemplateBlock,
} from "../types";
import { localizedMenuItems } from "./shopStorefrontCore";
import {
    SHOP_NAVIGATION_MENU_LOCATION_FALLBACKS,
    resolveShopNavigationSettings,
} from "./shopHeader";

function localizedNavigationMenus(
    basicData: PublicShopBasicData | null | undefined,
): PublicShopMenu[] {
    return (basicData?.menus ?? []).map((menu) => ({
        ...menu,
        items: localizedMenuItems(menu.items, menu.languageCode),
    }));
}

function localizedNavigationPages(
    pages: readonly PageLink[],
    languageCode: string | null | undefined,
): PageLink[] {
    const normalizedLanguage = languageCode?.trim().toLowerCase() ?? "";
    const matchingPages = normalizedLanguage === ""
        ? pages
        : pages.filter((page) => (
            !page.languageCode
            || page.languageCode.trim().toLowerCase() === normalizedLanguage
        ));
    const candidates = matchingPages.length > 0 ? matchingPages : pages;

    return candidates.map((page) => {
        const rawHref = page.href
            ?? (page.isHomepage || page.slug.trim() === ""
                ? "/"
                : page.slug.startsWith("/") ? page.slug : `/${page.slug}`);

        return {
            ...page,
            href: localizedStorefrontHref(rawHref, languageCode),
        };
    });
}

/** Resolves the shared source contract into a localized Shop navigation tree. */
export function resolveShopRuntimeNavigationItems({
    basicData,
    header,
    languageCode,
    pages,
    viewport,
}: {
    basicData: PublicShopBasicData | null | undefined;
    header: ShopTemplateBlock | null | undefined;
    languageCode: string | null | undefined;
    pages: readonly PageLink[];
    viewport: "desktop" | "mobile";
}): PublicShopMenuItem[] {
    const menuBlock = findSiteNavigationBlock(header) as ShopTemplateBlock | null;
    const settings = resolveShopNavigationSettings(menuBlock);
    const authoredItems = localizedMenuItems(
        siteNavigationAuthoredItems(menuBlock, {
            excludeRuntimeSources: true,
        }),
        languageCode,
    );

    return [...resolveSiteNavigationItems({
        authoredItems,
        fallbackLocations: SHOP_NAVIGATION_MENU_LOCATION_FALLBACKS[viewport],
        languageCode,
        menus: localizedNavigationMenus(basicData),
        pages: localizedNavigationPages(pages, languageCode),
        settings,
        viewport,
    })];
}
